import { FaSolidArrowLeft, FaSolidArrowRight, FaSolidTrash } from "solid-icons/fa"
import { Component, createEffect, createMemo, createSignal, For, Show, useContext } from "solid-js"
import { Engine } from "../../lib/core/engine/Engine.type"
import { SelectionMode } from "../../lib/core/engine/SelectionMode/SelectionMode.enum"
import entityTypeMetaList from "../../lib/core/entity/entityTypeMetaList.generated"
import { Entity } from "../../lib/core/entity/index"
import type { EntityTypeMeta } from "../../lib/core/EntityTypeMeta"
import findByIdOrThrow from "../../lib/core/findByIdOrThrow"
import findByPosition from "../../lib/core/findByPosition"
import { immutableEmptyArray } from "../../lib/lib"
import { createSharedTileGraphNodeCollectionMemo } from "../../rx/memo/createSharedTileGraphNodeCollectionMemo"
import { useSelectedPositionListSignal } from "../../rx/signal/useSelectedPositionListSignal"
import { useSelectedPositionSignal } from "../../rx/signal/useSelectedPositionSignal"
import useSelectionModeSignal from "../../rx/signal/useSelectionModeSignal"
import EngineContext from "../EngineContext"
import EmSmallTextMuted from "../Util/EmSmallTextMuted"
import SingleEntityIcon3D from "./SingleEntityIcon3D"

const InspectTileWidget: Component = () => {
  const engine: Engine = useContext(EngineContext)
  // const [getSelectedTool] = useSelectedToolSignal(engine)
  const getTpList = createSharedTileGraphNodeCollectionMemo(engine)
  const [getSelectedPosition] = useSelectedPositionSignal(engine)
  const [getSelectionMode] = useSelectionModeSignal(engine)
  const [getSelectedPositionList, setSelectedPositionList] = useSelectedPositionListSignal(engine)
  const [currentIndex, setCurrentIndex] = createSignal(0)
  
  // Reset index when selection changes
  createEffect(() => {
    const positionList = getSelectedPositionList()
    const index = currentIndex()
    if (index >= positionList.length) {
      setCurrentIndex(0)
    }
  })
  
  const getCurrentPosition = createMemo(() => {
    const selectionMode = getSelectionMode()
    if (selectionMode === SelectionMode.MultiSelect) {
      const positionList = getSelectedPositionList()
      const index = currentIndex()
      if (positionList.length > 0 && index < positionList.length) {
        return positionList[index]
      }
      return null
    }
    return getSelectedPosition()
  })
  
  const getEntityList = createMemo<Array<Entity>>(() => {
    const currentPosition = getCurrentPosition()
    if (currentPosition) {
      const tileGraphNode = findByPosition(getTpList(), currentPosition)
      if (tileGraphNode) {
        return tileGraphNode.ents()
      }
    }
    return immutableEmptyArray as unknown as Array<Entity>
  })

  const navigateToPrevious = () => {
    const positionList = getSelectedPositionList()
    if (positionList.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + positionList.length) % positionList.length)
    }
  }

  const navigateToNext = () => {
    const positionList = getSelectedPositionList()
    if (positionList.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % positionList.length)
    }
  }

  const removeCurrentPosition = () => {
    const currentPosition = getCurrentPosition()
    if (currentPosition) {
      const positionList = getSelectedPositionList()
      const newPositionList = positionList.filter(pos => 
        pos.x !== currentPosition.x || pos.y !== currentPosition.y
      )
      setSelectedPositionList(newPositionList)
      
      // Adjust current index if needed
      const index = currentIndex()
      if (index >= newPositionList.length && newPositionList.length > 0) {
        setCurrentIndex(newPositionList.length - 1)
      } else if (newPositionList.length === 0) {
        setCurrentIndex(0)
      }
    }
  }

  const hasSelectedPositions = createMemo(() => {
    const selectionMode = getSelectionMode()
    return selectionMode === SelectionMode.MultiSelect && getSelectedPositionList().length > 0
  })

  const hasMoreThanOneSelected = createMemo(() => {
    const selectionMode = getSelectionMode()
    return selectionMode === SelectionMode.MultiSelect && getSelectedPositionList().length > 1
  })

  return <div id="MapEditorInspectTileWidget" class="card-body">
    <div>
      <Show
        when={getCurrentPosition()}
        children={(selectedPosition) => {
          return (<div>
            <p class="text-monospace">
              <span class="badge bg-secondary">[{selectedPosition().x}, {selectedPosition().y}]</span>
              <span class="badge bg-secondary">[Entities: {getEntityList().length}]</span>
              {hasMoreThanOneSelected() && (
                <span class="badge bg-info ms-2">
                  {currentIndex() + 1} of {getSelectedPositionList().length} selected
                </span>
              )}
            </p>
            
            {hasMoreThanOneSelected() && (
              <div class="d-flex gap-2 mb-2">
                <button 
                  class="btn btn-sm btn-outline-secondary"
                  onClick={navigateToPrevious}
                >
                  <FaSolidArrowLeft />
                </button>
                <button 
                  class="btn btn-sm btn-outline-secondary"
                  onClick={navigateToNext}
                >
                  <FaSolidArrowRight />
                </button>
              </div>
            )}
            
            {hasSelectedPositions() && (
              <div class="mb-2">
                <button 
                  class="btn btn-sm btn-outline-danger"
                  onClick={removeCurrentPosition}
                  title="Remove current position from selection"
                >
                  <FaSolidTrash /> Remove from Selection
                </button>
              </div>
            )}
            
            <For each={getEntityList()}>{(entity: Entity) => {
            const entType: EntityTypeMeta = findByIdOrThrow(entityTypeMetaList, entity.etype_id)
            return <p>
              {entType.dname}
              <br />
              {/* <SingleEntityIcon3D entity={entity} size={64} /> */}
              <SingleEntityIcon3D entity={entity} size={256} style={{border: '1px dotted white'}} />
              {'hp' in entity && <p>HP: {entity.hp}</p>}
              {'fuel' in entity && <p>Fuel: {entity.fuel}</p>}
              {'ap' in entity && <p>Annex Points: {entity.ap}</p>}
              {'floor_e' in entity && <p>Floor Elevation: {entity.floor_e}</p>}
              {'water_e' in entity && <p>Water Elevation: {entity.water_e}</p>}
            </p>
          }}</For>
          </div>)
        }}
        fallback={EmSmallTextMuted('Select a tile to inspect.')}
      />
    </div>
  </div>
}

export default InspectTileWidget
